import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import { COLORS, FONTS } from '../theme';
import { NAV, SCHOOL, IMAGES } from '../data/siteData';
import { Icon, useScrollY } from './common';

const LETTER_COLORS = [COLORS.pink, COLORS.blue, COLORS.green, COLORS.orange, COLORS.purple, COLORS.red, COLORS.pink, COLORS.blue];

export function ScrollProgress() {
  const y = useScrollY();
  const max = typeof document !== 'undefined' ? document.documentElement.scrollHeight - window.innerHeight : 1;
  const pct = max > 0 ? Math.min(100, (y / max) * 100) : 0;
  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 5, zIndex: 1400, bgcolor: 'transparent' }}>
      <Box
        sx={{
          height: '100%',
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.yellow}, ${COLORS.green}, ${COLORS.blue}, ${COLORS.purple})`,
          borderRadius: '0 6px 6px 0',
        }}
      />
    </Box>
  );
}

function Brand() {
  const letters = "St Lord's".split('');
  return (
    <Box component="a" href="#home" sx={{ display: 'flex', alignItems: 'center', gap: 1.2, textDecoration: 'none' }}>
      {IMAGES.logo ? (
        <Box
          component="img"
          src={IMAGES.logo}
          alt="St Lord's logo"
          sx={{ width: 50, height: 50, borderRadius: '50%', objectFit: 'contain', bgcolor: '#fff', border: `2.5px solid ${COLORS.yellow}`, p: 0.2 }}
        />
      ) : (
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: `radial-gradient(circle at 30% 30%, ${COLORS.blue}, ${COLORS.navy})`,
            border: `3px solid ${COLORS.yellow}`,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: FONTS.heading,
            fontWeight: 800,
            fontSize: 20,
          }}
        >
          SL
        </Box>
      )}
      <Box sx={{ lineHeight: 1 }}>
        <Typography component="div" sx={{ fontFamily: FONTS.heading, fontWeight: 800, fontSize: 28, lineHeight: 1 }}>
          {letters.map((ch, i) => (
            <Box component="span" key={i} sx={{ color: LETTER_COLORS[i % LETTER_COLORS.length], whiteSpace: 'pre' }}>
              {ch}
            </Box>
          ))}
        </Typography>
        <Typography component="div" sx={{ fontFamily: FONTS.heading, fontWeight: 700, fontSize: 13, color: COLORS.navy, letterSpacing: 1.2 }}>
          International Public School
        </Typography>
      </Box>
    </Box>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 40 });

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: '#fff',
          color: COLORS.navy,
          boxShadow: scrolled ? '0 8px 24px rgba(27,42,107,.14)' : 'none',
          transition: 'box-shadow .3s ease',
          zIndex: 1300,
        }}
      >
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            bgcolor: COLORS.navy,
            color: '#fff',
            height: scrolled ? 0 : 40,
            overflow: 'hidden',
            transition: 'height .3s ease',
          }}
        >
          <Container maxWidth="lg" sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 14, fontWeight: 700 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Icon name="clock" sx={{ fontSize: 18, color: COLORS.yellow }} /> {SCHOOL.timings}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Icon name="pin" sx={{ fontSize: 18, color: COLORS.yellow }} /> Cottonpete, Bengaluru
              </Box>
            </Box>
            <Box component="a" href={`tel:+${SCHOOL.phoneRaw}`} sx={{ color: '#fff', textDecoration: 'none', fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Icon name="phone" sx={{ fontSize: 18, color: COLORS.yellow }} /> {SCHOOL.phone}
            </Box>
          </Container>
        </Box>

        <Toolbar disableGutters sx={{ minHeight: { xs: 68, md: 76 } }}>
          <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Brand />
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              {NAV.map((item, i) => (
                <Button
                  key={item.id}
                  href={`#${item.id}`}
                  sx={{
                    color: COLORS.navy,
                    fontSize: 16,
                    px: 1.6,
                    '&:hover': { bgcolor: `${LETTER_COLORS[i % LETTER_COLORS.length]}22`, color: LETTER_COLORS[i % LETTER_COLORS.length] },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                href={`https://wa.me/${SCHOOL.phoneRaw}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  ml: 1.5,
                  px: 3,
                  py: 1,
                  fontSize: 16,
                  bgcolor: COLORS.pink,
                  boxShadow: `0 6px 0 #C2185B`,
                  '&:hover': { bgcolor: '#ff2f86', transform: 'translateY(2px)', boxShadow: `0 4px 0 #C2185B` },
                  transition: 'all .2s ease',
                }}
              >
                Enquire Now
              </Button>
            </Box>
            <IconButton aria-label="Open menu" onClick={() => setOpen(true)} sx={{ display: { lg: 'none' }, color: COLORS.navy, bgcolor: COLORS.cream }}>
              <Icon name="menu" />
            </IconButton>
          </Container>
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: 290, bgcolor: COLORS.cream } }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton aria-label="Close menu" onClick={() => setOpen(false)} sx={{ color: COLORS.navy }}>
            <Icon name="close" />
          </IconButton>
        </Box>
        <List sx={{ px: 2 }}>
          {NAV.map((item, i) => (
            <ListItemButton
              key={item.id}
              component="a"
              href={`#${item.id}`}
              onClick={() => setOpen(false)}
              sx={{ borderRadius: '20px', mb: 1, bgcolor: '#fff', borderLeft: `8px solid ${LETTER_COLORS[i % LETTER_COLORS.length]}` }}
            >
              <ListItemText primary={item.label} primaryTypographyProps={{ sx: { fontFamily: FONTS.heading, fontWeight: 800, color: COLORS.navy, fontSize: 18 } }} />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ px: 3, pt: 1 }}>
          <Button
            fullWidth
            variant="contained"
            href={`https://wa.me/${SCHOOL.phoneRaw}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ bgcolor: COLORS.pink, py: 1.3, fontSize: 17, '&:hover': { bgcolor: '#ff2f86' } }}
          >
            Enquire on WhatsApp
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
